/**
 * buildEventListItems — the event renderer's `toProps`: maps the event
 * data-model to the source-agnostic `ListItem[]` viewmodel that
 * `EntryList`/`EntryRow` bind. Companion to `buildEventSpans` (timeline
 * subcolumn) and `buildAllDayEventsProvider` (timeline strip); this surface
 * is the calendar-list aggregator's own cell body, which paints a vertical
 * list of events for the current column.
 *
 * Each `EventItemTimed` projects itself to the SDK-side `EventRowProps`
 * (calendar color, start instant, click handler); `fromRowProps` flattens
 * that single event projection into the `ListItem` viewmodel
 * (`calendarColor → accentColor`, `time → at`). List-level display context
 * (the `EntryDisplay`: `timeScale`/`use24Hour`/`dateFormat`) is NOT baked into each item — it
 * rides alongside as `EntryDisplay`.
 *
 * Lifted out of `CalendarListAliasPlugin.bindCell` so the aggregator no longer
 * owns event-specific mapping — the event renderer module does. Per
 * permissive-renderer-architecture migration plan §Phase D.
 */

import type { TimezoneRegion } from "@tento-chrono";
import type {
  CalendarsClient,
  DateFormat,
  EventsPlatform,
  TimeFormat,
} from "@tento-lona";
import type { EventItemTimed } from "@tento-lona/sheets";
import { EventItemPresentation } from "@tento-lona";
import type { ListItem } from "@tento-ui/components/entry-list/list-item";
import type { EventAccess } from "../../../sheet/gestures/event-access.ts";

export interface BuildEventListItemsInput {
  /** Events visible in the current column's UTC range, already
   *  filtered by category/visibility. */
  readonly events: readonly EventItemTimed[];
  /** Platform shim used by `EventItemTimed.rowProps`
   *  (Tooltip/dispatch surfaces). */
  readonly eventsPlatform: EventsPlatform;
  /** Column timezone — `rowProps` formats times against it. */
  readonly tz: TimezoneRegion;
  /** Calendar metadata source for color / display lookup. */
  readonly calendars: CalendarsClient;
  /** How times are written. */
  readonly timeFormat: TimeFormat;
  /** Date-label format for "month-day" / "weekday-month" presentation. */
  readonly dateFormat: DateFormat;
  /** The client's event categories and drafts (`client.events`): titles and colours. */
  readonly presentation: EventItemPresentation.Scope;
  /**
   * The access of the calendar list the events are shown through
   * (`EventAccess.fromListConfig`): a row's click opens a read-only panel
   * from a read-only list, whatever the event's calendar grants.
   */
  readonly list: EventAccess.List;
}

/** Flatten one event's SDK projection into the source-agnostic viewmodel. */
function fromRowProps(props: EventItemPresentation.RowProps): ListItem {
  return {
    id: props.id,
    title: props.title,
    isEditable: props.isEditable,
    accentColor: props.options.calendarColor,
    at: props.options.time,
    timezone: props.options.tz,
    onClick: props.options.onClick,
  };
}

function buildEventListItems(input: BuildEventListItemsInput): ListItem[] {
  const { events, eventsPlatform, tz, calendars, timeFormat, dateFormat, presentation, list } =
    input;
  return events.map((event) =>
    fromRowProps(
      EventItemPresentation.rowProps(event, presentation, eventsPlatform, tz, calendars, timeFormat, dateFormat, list),
    ),
  );
}

/** Builds the list items an event list shows from events and their context. */
export namespace EventListItems {
  /** events (+ context) → `ListItem[]`. */
  export const build = buildEventListItems;
  /** A single `EventRowProps` → `ListItem` (e.g. month-cell rows). */
  export const fromEventRowProps = fromRowProps;
}
